import './bootstrap';
import '../css/app.css';

import { createApp, h } from 'vue';
import * as Sentry from '@sentry/vue';
import i18n from './i18nSetup';

import { ZiggyVue } from '../../vendor/tightenco/ziggy/dist/index.esm.js';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import './icons';

import { Routes } from '@/Routes/Routes.js';
import { createPinia } from 'pinia'
import { useEntityCacheStore } from '@/Stores/entityCacheStore';
import { useUniversalChatStore } from '@/Stores/universalChatStore';
import { useUserStore } from '@/Stores/userStore';
import MainApp from './AppMain.vue';
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import RequestMonitor from '@/Utils/RequestMonitor';
import AudioPrefetchService from '@/Services/AudioPrefetchService';
import Siren, { NOTIFICATION_SOUNDS } from '@/Utils/Siren';
import axiosInstance from '@/Api/api';
import clarityService from '@/Services/clarity';

const options = {
    maxToasts: 20,
    newestOnTop: true,
    position: "top-center",
    hideProgressBar: true,
};

const pinia = createPinia()
const app = createApp(MainApp).use(i18n);
const router = Routes;

const isProduction = window.location.hostname === 'tavernofazoth.com';
if (isProduction) {
    Sentry.init({
        app,
        dsn: "https://7248a428db12d33d267a677ab7c15a05@o4504142072709120.ingest.us.sentry.io/4510899357286400",
        integrations: [
            Sentry.browserTracingIntegration({ router }),
        ],
        tracesSampleRate: 0,
        sendDefaultPii: true,
        trackComponents: true,
    });
}

app.use(pinia)
app.use(Toast, options)
app.use(ZiggyVue, Ziggy)
app.use(router)
app.component('fa', FontAwesomeIcon);
app.component('FontAwesomeIcon', FontAwesomeIcon);

// Initialize entity cache store
const entityCacheStore = useEntityCacheStore();
entityCacheStore.initialize();
entityCacheStore.initDebugShortcuts();

// Initialize Microsoft Clarity analytics
clarityService.initClarity();

// Initialize audio debug shortcuts
Siren.initDebugShortcuts();

// Initialize request monitor for idle detection
const requestMonitor = new RequestMonitor();
requestMonitor.attachToAxios(axiosInstance);

// Initialize universal chat store for authenticated users
const userStore = useUserStore();
const universalChatStore = useUniversalChatStore();

if (userStore.getAccessToken()) {
    universalChatStore.initializeChat();
    // Initialize alert sound preferences from localStorage
    userStore.initializeAlertSoundPreferences();
    // Initialize crystal state from localStorage
    userStore.initializeCrystalState();

    // Identify user in Clarity and Sentry
    const userId = userStore.getUserIdFromToken();
    const email = userStore.getEmailFromToken();
    clarityService.identifyUser(userId, email);
    clarityService.setSubscriptionTag(userStore.getSubscription());
    if (isProduction) {
        Sentry.setUser({ id: userId, email: email });
    }

    // Initialize audio prefetch service
    const audioPrefetchService = new AudioPrefetchService({ debug: false });
    audioPrefetchService.initialize(requestMonitor);

    // Queue all notification sounds for prefetching
    const allSounds = Object.values(NOTIFICATION_SOUNDS);
    audioPrefetchService.addToQueue(allSounds);

    // Expose for debugging (optional, can be removed in production)
    window.audioPrefetchService = audioPrefetchService;
    window.requestMonitor = requestMonitor;
}

app.mount('#app');

